import { isTypingTarget } from '@/lib/typing-target';

/**
 * ⌘F / Ctrl+F to a page's search box (`SearchInput findShortcut`, on by default in AddFilterBar's
 * search). The logic, kept apart from the DOM so Jest can check it; `useFindShortcut` wires it up.
 */

/** A box inside one of these answers only while it's the open layer; one outside, only while none is open. */
export const FIND_LAYER = '[role="dialog"], [role="alertdialog"]';

export const isMacPlatform = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

/** The shortcut as the key chip and hints show it. */
export const findShortcutLabel = (): string => (isMacPlatform() ? '⌘F' : 'Ctrl+F');

export function isFindKey(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): boolean {
  return event.key.toLowerCase() === 'f' && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;
}

/** What the logic needs of a search box's input (an HTMLInputElement, or a stand-in in tests). */
export type FindBox<Self> = {
  isConnected: boolean;
  /** None while it or a parent is hidden (`display: none`, a pane hidden at this width). */
  getClientRects(): { length: number };
  closest(selector: string): unknown;
  compareDocumentPosition(other: Self): number;
};

/** A menu or list that holds the focus (a dropdown menu, a select): the key is left to the browser. */
const FOCUS_HOLDER = '[role="menu"], [role="menubar"], [role="listbox"]';

/** `compareDocumentPosition`: the other node comes first. */
const PRECEDING = 2;

/**
 * The box that answers in a layer (an open dialog, or null for the page): the first shown, in the
 * page's order. A hidden one (the Inbox list behind an open conversation) is skipped.
 */
export function firstFindBox<T extends FindBox<T>>(boxes: Iterable<T>, layer: unknown): T | null {
  let first: T | null = null;
  for (const box of boxes) {
    if (!box.isConnected || !box.getClientRects().length || (box.closest(FIND_LAYER) ?? null) !== layer) continue;
    if (!first || first.compareDocumentPosition(box) & PRECEDING) first = box;
  }
  return first;
}

/**
 * The box a key press should go to, or null to leave the press to the browser: not the shortcut,
 * no box in the open layer (a task or dialog open over the page), a second press in the box itself
 * (the browser's own find), typing in another field, or the focus in a menu (it would keep it).
 */
export function findShortcutTarget<T extends FindBox<T>>(
  event: Parameters<typeof isFindKey>[0] & { target: EventTarget | null; defaultPrevented: boolean },
  boxes: Iterable<T>,
  openLayer: unknown
): T | null {
  if (event.defaultPrevented || !isFindKey(event)) return null;
  const box = firstFindBox(boxes, openLayer);
  if (!box || (event.target as unknown) === box) return null;
  if (isTypingTarget(event)) return null;
  const target = event.target as { closest?: (selector: string) => unknown } | null;
  if (target?.closest?.(FOCUS_HOLDER)) return null;
  return box;
}
